import { Fragment, useMemo } from 'react'

interface HighlightedMarkupProps {
  /** A single HTML tag string, e.g. `<div data-x="y" async></div>`. */
  markup: string
}

const TAG_COLOR = 'text-blue-400'
const ATTR_COLOR = 'text-blue-300'
const VALUE_COLOR = 'text-orange-300'
const PUNCT_COLOR = 'text-gray-500'

/**
 * Renders an HTML tag string with the same hand-tokenized syntax colors the
 * embed-script/container snippets use, so every install snippet reads as one
 * family. Deliberately small: it colors tag names, attribute names, quoted
 * values, and punctuation — it is not a general HTML parser.
 */
export function HighlightedMarkup({ markup }: HighlightedMarkupProps) {
  const tokens = useMemo(() => tokenizeTag(markup), [markup])

  return (
    <code className="text-sm font-mono text-gray-300 break-all whitespace-pre-wrap">
      {tokens.map((token, i) => (
        <Fragment key={`${token.className}-${i}`}>
          <span className={token.className}>{token.text}</span>
        </Fragment>
      ))}
    </code>
  )
}

interface Token {
  text: string
  className: string
}

/**
 * Splits an HTML tag string into colored tokens. Regex-based on purpose — the
 * inputs are the fixed snippets we generate, not arbitrary user HTML.
 */
function tokenizeTag(markup: string): Token[] {
  const tokens: Token[] = []

  // One matcher per token kind, tried left-to-right against the remaining
  // string. Order matters: attribute="value" before bare attribute.
  const matchers: Array<{
    re: RegExp
    classify: (m: RegExpExecArray) => Token[]
  }> = [
    {
      // `<tag` or `</tag`
      re: /^(<\/?)([a-zA-Z][\w-]*)/,
      classify: (m) => [
        { text: m[1], className: PUNCT_COLOR },
        { text: m[2], className: TAG_COLOR },
      ],
    },
    {
      // ` attr="value"`
      re: /^(\s+)([\w-]+)(=)("[^"]*")/,
      classify: (m) => [
        { text: m[1], className: PUNCT_COLOR },
        { text: m[2], className: ATTR_COLOR },
        { text: m[3], className: PUNCT_COLOR },
        { text: m[4], className: VALUE_COLOR },
      ],
    },
    {
      // ` attr` (valueless, e.g. async)
      re: /^(\s+)([\w-]+)(?=[\s>])/,
      classify: (m) => [
        { text: m[1], className: PUNCT_COLOR },
        { text: m[2], className: ATTR_COLOR },
      ],
    },
    {
      // `>` or `/>`
      re: /^(\/?>)/,
      classify: (m) => [{ text: m[1], className: PUNCT_COLOR }],
    },
  ]

  let rest = markup

  while (rest.length > 0) {
    let matched = false

    for (const { re, classify } of matchers) {
      const m = re.exec(rest)
      if (m) {
        tokens.push(...classify(m))
        rest = rest.slice(m[0].length)
        matched = true
        break
      }
    }

    // Anything we don't recognize (stray whitespace/text): emit one char as
    // punctuation and advance, so we can never loop forever on odd input.
    if (!matched) {
      tokens.push({ text: rest[0], className: PUNCT_COLOR })
      rest = rest.slice(1)
    }
  }

  return tokens
}
